/** App shell: sidebar on desktop, a compact bar with tabs on a phone. */

import { Boxes, LogOut, ServerCog, Users } from "lucide-react";
import { NavLink, Outlet } from "react-router";
import { Avatar, Logo } from "@/components/display";
import { useMe, useSignOut } from "@/features/session/hooks";
import { Button, focusRing } from "./button";
import { Toaster } from "./toaster";

const navItems = [
  { to: "/people", label: "People", icon: Users },
  { to: "/workspaces", label: "Workspaces", icon: Boxes },
  { to: "/services", label: "Services", icon: ServerCog },
];

const linkClass = ({ isActive }: { isActive: boolean }) =>
  `flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors pointer-coarse:py-3 ${focusRing} ${
    isActive
      ? "bg-white text-zinc-950 shadow-xs ring-1 ring-zinc-200 dark:bg-zinc-800 dark:text-white dark:ring-zinc-700"
      : "text-zinc-600 hover:bg-zinc-200/60 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-800/60 dark:hover:text-zinc-100"
  }`;

export function Layout() {
  const { data: me } = useMe();
  const signOut = useSignOut();

  return (
    <div className="min-h-screen md:flex">
      <aside className="border-b border-zinc-200 bg-zinc-100/70 md:sticky md:top-0 md:flex md:h-screen md:w-60 md:shrink-0 md:flex-col md:border-r md:border-b-0 dark:border-zinc-800 dark:bg-zinc-900/50">
        <div className="flex items-center justify-between px-4 py-3 md:px-5 md:py-5">
          <Logo />
          <Button tone="ghost" aria-label="Sign out" onClick={() => signOut.mutate()} className="w-9 px-0 md:hidden">
            <LogOut size={16} />
          </Button>
        </div>
        <nav aria-label="Main" className="grid grid-cols-3 gap-1 px-2 pb-2 md:flex md:flex-col md:px-3">
          {navItems.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} className={(s) => `${linkClass(s)} justify-center md:justify-start`}>
              <Icon size={16} className="shrink-0" />
              {label}
            </NavLink>
          ))}
        </nav>
        {me && (
          <div className="mt-auto hidden items-center gap-3 border-t border-zinc-200 px-4 py-4 md:flex dark:border-zinc-800">
            <Avatar name={me.name} email={me.email} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{me.name || me.email}</p>
              <p className="truncate text-xs text-zinc-500">{me.email}</p>
            </div>
            <Button tone="ghost" aria-label="Sign out" onClick={() => signOut.mutate()} className="w-9 px-0">
              <LogOut size={16} />
            </Button>
          </div>
        )}
      </aside>

      <main className="min-w-0 flex-1 px-4 py-6 md:px-10 md:py-10">
        <div className="mx-auto max-w-5xl">
          <Outlet />
        </div>
      </main>

      <Toaster />
    </div>
  );
}
